/**
 * Tile (homepage) - image-centric redesign per Bowz's Backlog brief
 * ("Homepage Tile Grid REDESIGN", 2026-06-10):
 *
 *   - Game NAME label rendered ABOVE the tile.
 *   - Tile itself is a square card (160 mobile / 180 desktop) with the
 *     game PNG centred at 12% padding so the card colour forms a frame.
 *   - The DESCRIPTION (tagline) is hidden by default and fades in:
 *       - on `:hover`           (desktop / pointer:fine)
 *       - on `:active`          (mobile tap-and-hold)
 *       - and on first tap      (we add `data-revealed=true` for 1s)
 *   - Click model:
 *       - Desktop (hover-capable): single click navigates.
 *       - Mobile / touch (no hover): the first tap reveals the description
 *         and arms a 3s "pending" window. A second tap inside that window
 *         navigates. Tap-and-hold also reveals without navigating.
 *   - REMOVED: "Soon" badge, Play CTA, fire/streak icon.
 *   - Disabled (`enabled: false`) tiles render at 50% opacity and are
 *     non-interactive (no navigation; description still revealable).
 *
 * PM 2026-06-12 (completion overlay timing):
 *   The COMPLETION result panel (SOLVED / score / time / rank, painted by
 *   `tile.ts`'s `gv-tile-completion` element) used to ride on the CSS
 *   `:hover` cascade which means it hid the moment the cursor left the
 *   card. Bowz wanted readers a longer beat to actually parse the panel,
 *   plus a clean cross-tile dismiss so the grid never shows two stacked
 *   panels at once. We now drive completion overlay visibility from a
 *   tiny module-level state machine:
 *
 *     - mouseenter / pointerdown / touchstart on a completed tile -> show
 *       the panel by stamping `data-showing-completion="true"` on the
 *       card and start a 6s auto-hide timer.
 *     - mouseenter / pointerdown / touchstart on ANY OTHER tile (even an
 *       incomplete one) -> immediately hide the previously-shown tile and
 *       (if the new tile is also completed) re-arm the 6s window for it.
 *     - The timer fires -> CSS-transition the panel out.
 *
 *   The tagline overlay (`gv-tile-overlay`) still uses the simple `:hover`
 *   cascade. Only the COMPLETION overlay timing is state-machine driven.
 */

export interface TileProps {
  gameId: string;
  name: string;
  /** Short description shown in the hover/tap overlay. */
  tagline: string;
  /** Path to the square PNG (e.g. `/tiles/connections.png`). Optional - a
   *  placeholder is rendered when omitted or empty. */
  tileImagePath?: string;
  /** When false the tile is greyed out and navigation is suppressed. */
  enabled?: boolean;
  /**
   * Optional "today's outcome" overlay (PM 2026-06-11). Set when the player
   * has finished the current period's puzzle for this game. Renders a small
   * status dot in the top-right corner and replaces the hover/tap tagline
   * with a result panel showing the primary stat, time, and rank placeholder.
   * The card itself stays clickable so tapping a finished tile still lands
   * on the inline result card.
   */
  completion?: TileCompletionProps;
  onClick: () => void;
}

export interface TileCompletionProps {
  /**
   * `won` paints the success accent, `lost` / `timed_out` paint the failure
   * accent. Mirrors the modal / inline-card outcome shape exactly.
   */
  outcome: 'won' | 'lost' | 'timed_out';
  /** The snapshot's `stats.primary` text - e.g. "FOUND 14 / 273". */
  primary: string;
  /** The snapshot's `stats.time` text - e.g. "05:51". */
  time: string;
  /** "Rank: TBD" placeholder until Phase 19 ships leaderboards. */
  rank?: string;
}

export interface TileHandle {
  update: (props: TileProps) => void;
  destroy: () => void;
}

interface InternalRefs {
  root: HTMLDivElement;
  name: HTMLSpanElement;
  card: HTMLButtonElement;
  image: HTMLImageElement;
  placeholder: HTMLSpanElement;
  overlay: HTMLDivElement;
  overlayText: HTMLSpanElement;
  completion: HTMLDivElement;
  completionBadge: HTMLSpanElement;
  completionOutcome: HTMLParagraphElement;
  completionPrimary: HTMLParagraphElement;
  completionTime: HTMLParagraphElement;
  completionRank: HTMLParagraphElement;
}

function buildSkeleton(): InternalRefs {
  const root = document.createElement('div');
  root.className = 'gv-tile-wrap';

  const name = document.createElement('span');
  name.className = 'gv-tile-name';
  root.appendChild(name);

  const card = document.createElement('button');
  card.type = 'button';
  card.className = 'gv-tile';
  root.appendChild(card);

  const image = document.createElement('img');
  image.className = 'gv-tile-image';
  image.alt = '';
  image.decoding = 'async';
  // Eager-load (not lazy): the homepage tile grid is above the fold on
  // every viewport we care about (8 tiles in a 2x4 grid fits inside a
  // typical phone scroll without scrolling on most devices). Lazy was
  // contributing to PM's blank-tile report on mobile because the IO
  // observer can race the layout and the tile collapses to zero before
  // the fetch kicks in.
  image.loading = 'eager';
  card.appendChild(image);

  const placeholder = document.createElement('span');
  placeholder.className = 'gv-tile-placeholder';
  placeholder.hidden = true;
  card.appendChild(placeholder);

  const overlay = document.createElement('div');
  overlay.className = 'gv-tile-overlay';
  card.appendChild(overlay);

  const overlayText = document.createElement('span');
  overlayText.className = 'gv-tile-overlay-text';
  overlay.appendChild(overlayText);

  // Completion panel - hidden until `completion` props are passed. Lives
  // INSIDE the card so it inherits the rounded clip and tap target. The
  // small status dot (`completionBadge`) sits in the top-right corner of
  // the card itself (positioned absolutely via CSS).
  const completionBadge = document.createElement('span');
  completionBadge.className = 'gv-tile-completion-badge';
  completionBadge.hidden = true;
  completionBadge.setAttribute('aria-hidden', 'true');
  card.appendChild(completionBadge);

  const completion = document.createElement('div');
  completion.className = 'gv-tile-completion';
  completion.hidden = true;
  card.appendChild(completion);

  const completionOutcome = document.createElement('p');
  completionOutcome.className = 'gv-tile-completion-outcome';
  completion.appendChild(completionOutcome);

  const completionPrimary = document.createElement('p');
  completionPrimary.className = 'gv-tile-completion-primary';
  completion.appendChild(completionPrimary);

  const completionTime = document.createElement('p');
  completionTime.className = 'gv-tile-completion-time';
  completion.appendChild(completionTime);

  const completionRank = document.createElement('p');
  completionRank.className = 'gv-tile-completion-rank';
  completion.appendChild(completionRank);

  return {
    root,
    name,
    card,
    image,
    placeholder,
    overlay,
    overlayText,
    completion,
    completionBadge,
    completionOutcome,
    completionPrimary,
    completionTime,
    completionRank,
  };
}

/* ---------------------------------------------------------------------- */
/* Module-level completion-overlay state machine (PM 2026-06-12).         */
/*                                                                        */
/* Only ONE completion overlay may be visible at a time across the entire */
/* tile grid. Showing on tile A:                                          */
/*   - clears tile B's `data-showing-completion`                          */
/*   - cancels the previous auto-hide timer                               */
/*   - stamps `data-showing-completion="true"` on tile A                  */
/*   - schedules a 6_000ms timer that clears the stamp                    */
/*                                                                        */
/* This lives at module scope (not inside `mountTile`) so EVERY tile      */
/* shares the same `currentlyShownCard` + `hideTimerId`.                  */
/* ---------------------------------------------------------------------- */

const COMPLETION_OVERLAY_TTL_MS = 6_000;
let currentlyShownCard: HTMLElement | null = null;
let hideTimerId: number | null = null;

function hideCompletionOverlayImmediately(): void {
  if (currentlyShownCard) {
    currentlyShownCard.removeAttribute('data-showing-completion');
    currentlyShownCard = null;
  }
  if (hideTimerId !== null) {
    window.clearTimeout(hideTimerId);
    hideTimerId = null;
  }
}

function showCompletionOverlay(card: HTMLElement): void {
  if (currentlyShownCard && currentlyShownCard !== card) {
    currentlyShownCard.removeAttribute('data-showing-completion');
  }
  if (hideTimerId !== null) {
    window.clearTimeout(hideTimerId);
    hideTimerId = null;
  }
  card.setAttribute('data-showing-completion', 'true');
  currentlyShownCard = card;
  hideTimerId = window.setTimeout(() => {
    if (currentlyShownCard === card) {
      card.removeAttribute('data-showing-completion');
      currentlyShownCard = null;
    }
    hideTimerId = null;
  }, COMPLETION_OVERLAY_TTL_MS);
}

export function mountTile(target: HTMLElement, props: TileProps): TileHandle {
  const refs = buildSkeleton();
  let current = props;

  // First-tap state machine (touch / pointer:coarse only).
  let pendingArmed = false;
  let pendingTimer: number | null = null;
  let revealTimer: number | null = null;

  function clearTimers(): void {
    if (pendingTimer !== null) {
      window.clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    if (revealTimer !== null) {
      window.clearTimeout(revealTimer);
      revealTimer = null;
    }
  }

  function hasCompletion(): boolean {
    return current.enabled !== false && current.completion !== undefined;
  }

  // Show this tile's completion overlay (if it has one). Hovering or
  // tapping any OTHER tile - even an incomplete one - dismisses the
  // currently-shown overlay. So we split the logic: the "any tile" entry
  // point always notifies the shared state machine, which then either
  // re-targets to this tile (if it has a completion to show) or hides
  // the previous tile.
  const handleTileEnter = (): void => {
    if (current.enabled === false) {
      // Disabled tile - if some other completion is currently shown,
      // dismiss it (cross-tile dismiss rule), but don't show anything.
      if (currentlyShownCard && currentlyShownCard !== refs.card) {
        hideCompletionOverlayImmediately();
      }
      return;
    }
    if (hasCompletion()) {
      showCompletionOverlay(refs.card);
    } else if (currentlyShownCard && currentlyShownCard !== refs.card) {
      // Hovering an incomplete tile while another tile's overlay is
      // visible -> immediately hide the other one.
      hideCompletionOverlayImmediately();
    }
  };

  // Desktop hover (pointer:fine devices). mouseenter doesn't fire on
  // touch-only devices; we cover those via pointerdown / touchstart below.
  refs.card.addEventListener('mouseenter', handleTileEnter);
  // Touch / coarse pointer entry. `pointerdown` covers most modern touch
  // browsers; `touchstart` is the legacy belt-and-braces fallback. We use
  // {passive: true} so the browser can still scroll the grid.
  refs.card.addEventListener('pointerdown', handleTileEnter);
  refs.card.addEventListener('touchstart', handleTileEnter, { passive: true });

  function setRevealed(on: boolean): void {
    if (on) {
      refs.card.setAttribute('data-revealed', 'true');
    } else {
      refs.card.removeAttribute('data-revealed');
    }
  }

  // Desktop hover already exposes the overlay via CSS - we don't fight it.
  // We use matchMedia at click-time to detect touch / no-hover devices.
  function isTouchPrimary(): boolean {
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  function navigate(): void {
    current.onClick();
  }

  const handleClick = (ev: MouseEvent): void => {
    if (current.enabled === false) {
      ev.preventDefault();
      return;
    }
    if (!isTouchPrimary()) {
      // Hover-capable device: single click navigates (overlay already shows
      // on hover via CSS).
      navigate();
      return;
    }
    // Touch / coarse pointer: two-tap-to-load.
    if (pendingArmed) {
      clearTimers();
      pendingArmed = false;
      setRevealed(false);
      navigate();
      return;
    }
    pendingArmed = true;
    setRevealed(true);
    // Hide the overlay after 1s (description visible window).
    revealTimer = window.setTimeout(() => {
      setRevealed(false);
      revealTimer = null;
    }, 1000);
    // Disarm the "second tap navigates" window after 3s.
    pendingTimer = window.setTimeout(() => {
      pendingArmed = false;
      pendingTimer = null;
    }, 3000);
  };

  refs.card.addEventListener('click', handleClick);

  function render(p: TileProps): void {
    current = p;
    const enabled = p.enabled !== false;
    refs.root.setAttribute('data-game-id', p.gameId);
    refs.root.setAttribute('data-enabled', enabled ? 'true' : 'false');
    refs.card.setAttribute('data-game-id', p.gameId);
    refs.card.setAttribute('data-enabled', enabled ? 'true' : 'false');
    refs.card.setAttribute('aria-label', `Play ${p.name}`);
    refs.card.disabled = !enabled;

    refs.name.textContent = p.name;
    refs.overlayText.textContent = p.tagline;

    if (p.tileImagePath && p.tileImagePath.length > 0) {
      refs.image.src = p.tileImagePath;
      refs.image.alt = `${p.name} tile`;
      refs.image.hidden = false;
      refs.placeholder.hidden = true;
      refs.placeholder.textContent = '';
      // If the image fails to load, fall back to the placeholder.
      refs.image.onerror = (): void => {
        refs.image.hidden = true;
        refs.placeholder.hidden = false;
        refs.placeholder.textContent = initials(p.name);
      };
    } else {
      refs.image.removeAttribute('src');
      refs.image.hidden = true;
      refs.placeholder.hidden = false;
      refs.placeholder.textContent = initials(p.name);
    }

    // Completion overlay - only meaningful on playable tiles. We deliberately
    // suppress the badge for dormant (`enabled: false`) tiles because a stale
    // snapshot from a prior life of the registration could otherwise leak
    // through. See the PM 2026-06-11 "Bonus: dormant tiles" note in the spec.
    const showCompletion = enabled && p.completion !== undefined;
    if (showCompletion && p.completion) {
      const outcomeAttr = p.completion.outcome === 'won' ? 'won' : 'lost';
      refs.completion.hidden = false;
      refs.completion.setAttribute('data-outcome', outcomeAttr);
      refs.completionBadge.hidden = false;
      refs.completionBadge.setAttribute('data-outcome', outcomeAttr);
      refs.completionOutcome.textContent =
        p.completion.outcome === 'won'
          ? 'Solved'
          : p.completion.outcome === 'timed_out'
            ? 'Time up'
            : 'Not solved';
      refs.completionPrimary.textContent = p.completion.primary;
      refs.completionTime.textContent = p.completion.time;
      refs.completionRank.textContent = p.completion.rank ?? 'Rank: TBD';
      refs.card.setAttribute('data-completion', outcomeAttr);
      refs.root.setAttribute('data-completion', outcomeAttr);
    } else {
      refs.completion.hidden = true;
      refs.completion.removeAttribute('data-outcome');
      refs.completionBadge.hidden = true;
      refs.completionBadge.removeAttribute('data-outcome');
      refs.completionOutcome.textContent = '';
      refs.completionPrimary.textContent = '';
      refs.completionTime.textContent = '';
      refs.completionRank.textContent = '';
      refs.card.removeAttribute('data-completion');
      refs.root.removeAttribute('data-completion');
    }
  }

  render(props);
  target.appendChild(refs.root);

  return {
    update: (next: TileProps): void => {
      render(next);
    },
    destroy: (): void => {
      clearTimers();
      // If this tile currently owns the shared completion-overlay slot,
      // clear it so the module-level state doesn't reference a detached
      // node.
      if (currentlyShownCard === refs.card) {
        if (hideTimerId !== null) {
          window.clearTimeout(hideTimerId);
          hideTimerId = null;
        }
        currentlyShownCard = null;
      }
      refs.card.removeEventListener('click', handleClick);
      refs.card.removeEventListener('mouseenter', handleTileEnter);
      refs.card.removeEventListener('pointerdown', handleTileEnter);
      refs.card.removeEventListener('touchstart', handleTileEnter);
      refs.root.remove();
    },
  };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}
